import { classColorForCategory } from '@viewer/lib/classColors'
import {
  generateView2D,
  type Drawing2D,
  type View2DDef,
  type View2DModelSource,
} from './lib'

// ---------------------------------------------------------------------------
// Cola entre o núcleo puro das Vistas 2D e o modelo carregado no viewer.
//
// Resolve as duas coisas que o núcleo não sabe fazer sozinho:
//   - `categoryOf`: categoria IFC por localId. O Fragments 3.4.3 só dá
//     o mapa ao contrário (`getItemsOfCategories` devolve categoria →
//     ids), por isso inverte-se, exactamente como no export KMZ.
//   - `colorForCategory`: a paleta "cores por classe" do viewer, para o
//     desenho sair com as mesmas cores que o 3D.
//
// O mapa de categorias é caro (percorre o modelo todo) e não muda entre
// vistas do mesmo modelo: fica em cache por modelId, mas só vale para a
// MESMA instância do modelo. O modelId é o nome do ficheiro, e reabrir
// a revisão nova de um IFC com o mesmo nome dava as categorias da
// revisão anterior (localIds iguais, elementos diferentes).
// ---------------------------------------------------------------------------

interface CategorySource {
  modelId?: string
  getItemsOfCategories: (
    categories: RegExp[],
  ) => Promise<Record<string, number[]>>
}

const categoryCache = new Map<
  string,
  { source: object; map: Map<number, string> }
>()

/** Esquece a cache de um modelo (ou de todos). Chamar quando o modelo é
 *  descarregado, para não segurar mapas de modelos que já não existem. */
export function clearCategoryCache(modelId?: string): void {
  if (modelId) categoryCache.delete(modelId)
  else categoryCache.clear()
}

export async function buildCategoryMap(
  model: CategorySource,
): Promise<Map<number, string>> {
  const key = model.modelId ?? ''
  const cached = key ? categoryCache.get(key) : undefined
  if (cached && cached.source === model) return cached.map
  const map = new Map<number, string>()
  try {
    const byCat = await model.getItemsOfCategories([/.*/])
    for (const [cat, ids] of Object.entries(byCat)) {
      if (!ids) continue
      for (const id of ids) map.set(id, cat)
    }
  } catch {
    // Sem categorias o desenho sai numa camada única ('GERAL'); é uma
    // degradação aceitável, nunca um erro.
  }
  if (key) categoryCache.set(key, { source: model, map })
  return map
}

export interface GenerateForViewerOptions {
  bgMode?: 'dark' | 'light'
  includeProjection?: boolean
  maxTriangles?: number
  /** Esconder as linhas de projecção tapadas por geometria à frente. */
  hiddenLines?: boolean
}

/** Gera o desenho de uma vista sobre um modelo do viewer. */
export async function generateViewForModel(
  model: View2DModelSource & CategorySource,
  view: View2DDef,
  opts: GenerateForViewerOptions = {},
): Promise<Drawing2D> {
  const categories = await buildCategoryMap(model)
  const bg = opts.bgMode ?? 'light'
  return generateView2D(model, view, {
    categoryOf: (localId) => categories.get(localId) ?? null,
    // O desenho é para papel: a paleta de fundo claro é a que dá
    // contraste em folha branca, mesmo que o 3D esteja em modo escuro.
    colorForCategory: (cat) => classColorForCategory(cat, bg),
    includeProjection: opts.includeProjection !== false,
    maxTriangles: opts.maxTriangles,
    hiddenLines: opts.hiddenLines === true,
  })
}

/** Total de polilinhas de um desenho (corte + projecção). Serve para a
 *  UI dizer se a vista saiu vazia sem ter de andar a somar em vários
 *  sítios. */
export function drawingPolylineCount(d: Drawing2D): {
  cut: number
  projection: number
  total: number
} {
  let cut = 0
  let projection = 0
  for (const layer of d.layers) {
    cut += layer.cut.length
    projection += layer.projection.length
  }
  return { cut, projection, total: cut + projection }
}

/** Cópia do desenho só com as camadas indicadas. Usada pela
 *  pré-visualização e pela exportação quando o utilizador desliga
 *  camadas. Devolve o próprio desenho quando não há nada filtrado, para
 *  não copiar à toa. */
export function filterDrawingLayers(
  d: Drawing2D,
  visible: (layerName: string) => boolean,
): Drawing2D {
  const kept = d.layers.filter((l) => visible(l.name))
  if (kept.length === d.layers.length) return d
  return { ...d, layers: kept }
}
